import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { IconPlus, IconUpload, IconUserPlus } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';

interface IAddStudentMenuButtonProps {
  onSingleStudent: () => void;
  onBulkUpload: () => void;
  disabled?: boolean;
  dataTestId?: string;
}

const CLOSE_ALL_DROPDOWNS_EVENT = 'close-all-dropdowns';

const AddStudentMenuButton: React.FC<IAddStudentMenuButtonProps> = ({
  onSingleStudent,
  onBulkUpload,
  disabled = false,
  dataTestId = 'add-student-menu',
}) => {
  const { formatText } = useFormatText();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (disabled) return;

    if (!isOpen) {
      document.dispatchEvent(new CustomEvent(CLOSE_ALL_DROPDOWNS_EVENT));

      setTimeout(() => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          const scrollX = window.scrollX || window.pageXOffset;
          const scrollY = window.scrollY || window.pageYOffset;

          setPosition({
            top: rect.bottom + scrollY + 5,
            left: isRTL ? rect.left + scrollX : rect.right + scrollX,
          });
        }
        setIsOpen(true);
      }, 0);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleCloseAll = () => setIsOpen(false);

    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      document.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    };
  }, [isOpen]);

  const actions = [
    {
      key: 'single',
      label: formatText('classDetails.addStudentMenu.single'),
      icon: <IconUserPlus size={20} />,
      onClick: onSingleStudent,
    },
    {
      key: 'bulk',
      label: formatText('classDetails.addStudentMenu.bulk'),
      icon: <IconUpload size={20} />,
      onClick: onBulkUpload,
    },
  ];

  return (
    <div className="relative">
      <div ref={triggerRef} onClick={toggle}>
        <Button
          icon={<IconPlus size={20} />}
          label={formatText('classDetails.actions.addStudent')}
          variant="primary"
          className="px-4 h-12 gap-2 min-w-33.25"
          disabled={disabled}
          dataTestId={`${dataTestId}-trigger`}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={clsx(
              'fixed z-9999 flex flex-col bg-white border border-neutral-light-200 rounded-2xl shadow-lg p-2 min-w-52',
              !isRTL && '-translate-x-full'
            )}
            style={{
              top: position.top,
              left: position.left,
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${dataTestId}-menu`}
          >
            {actions.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={() => {
                  action.onClick();
                  setIsOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-brand-light-50 text-neutral-primary text-base font-medium transition-colors whitespace-nowrap cursor-pointer"
                data-testid={`${dataTestId}-item-${action.key}`}
              >
                <span className="flex items-center justify-center text-neutral-secondary">
                  {action.icon}
                </span>
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default AddStudentMenuButton;
